// SuppliersHub.js
// Full-page supplier/manufacturer directory. See docs/SUPPLIERS_MODULE.md.
// Left panel: searchable directory list with compliance status indicators.
// Right panel: Info, Compliance Documents (fixed + custom checklist), Ingredients (manufactures/supplies).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import supplierService from '../services/supplierService';
import documentService from '../services/documentService';
import documentCategoryService from '../services/documentCategoryService';
import ConfirmModal from './ConfirmModal';

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];

const STATUS_CFG = {
  satisfied: { cls: 'bg-success', label: 'On file' },
  expiring:  { cls: 'bg-warning text-dark', label: 'Expiring soon' },
  expired:   { cls: 'bg-danger', label: 'Expired' },
  missing:   { cls: 'bg-light text-secondary border', label: 'Missing' },
};

function StatusPill({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.missing;
  return <span className={`badge ${cfg.cls}`} style={{ fontSize: 11, fontWeight: 500 }}>{cfg.label}</span>;
}

// Same 60-day window as the backend's computeStatus() in supplierRoutes.js.
function expiryStatus(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = new Date(dateStr + 'T12:00:00');
  if (exp < today) return 'expired';
  if (exp <= new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000)) return 'expiring';
  return 'ok';
}

function ExpiryBadge({ date }) {
  const s = expiryStatus(date);
  if (!s) return null;
  const cfg = {
    expired:  { cls: 'bg-danger', label: 'Expired' },
    expiring: { cls: 'bg-warning text-dark', label: `Exp. ${date}` },
    ok:       { cls: 'bg-success', label: `Exp. ${date}` },
  }[s];
  return <span className={`badge ${cfg.cls}`} style={{ fontSize: 10 }}>{cfg.label}</span>;
}

const STATUS_ICON = { satisfied: '✓', expiring: '!', expired: '✕', missing: '–' };

// Only ever seeded for foreign/import suppliers (see FOREIGN_KEYS in
// backend/routes/supplierRoutes.js) — domestic suppliers show N/A in the overview matrix rather
// than a misleading "missing". Prior Notice isn't here — it's tracked per shipment now, not at
// the manufacturer level, so it never appears as a matrix column at all.
const FOREIGN_ONLY_KEYS = new Set(['fsvp_records']);

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.missing;
  return (
    <span
      className={`badge ${cfg.cls}`}
      title={cfg.label}
      style={{ width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, borderRadius: 6 }}
    >
      {STATUS_ICON[status] || STATUS_ICON.missing}
    </span>
  );
}

function NABadge() {
  return (
    <span
      className="badge bg-white text-muted border"
      title="Not applicable — domestic supplier"
      style={{ width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, borderRadius: 6 }}
    >
      N/A
    </span>
  );
}

function ComplianceLegend() {
  return (
    <div className="d-flex gap-3 flex-wrap px-1 pt-1">
      {Object.entries(STATUS_CFG).map(([status, cfg]) => (
        <div key={status} className="d-flex align-items-center gap-1">
          <span
            className={`badge ${cfg.cls}`}
            style={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, borderRadius: 5 }}
          >
            {STATUS_ICON[status]}
          </span>
          <span className="text-muted" style={{ fontSize: 12 }}>{cfg.label}</span>
        </div>
      ))}
      <div className="d-flex align-items-center gap-1">
        <span className="badge bg-white text-muted border" style={{ width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, borderRadius: 5 }}>
          N/A
        </span>
        <span className="text-muted" style={{ fontSize: 12 }}>Not applicable (domestic supplier)</span>
      </div>
    </div>
  );
}

// Manufacturer × document-type matrix — see docs/SUPPLIERS_MODULE.md. Each row expands to a
// sub-row listing that manufacturer's ingredients with their spec-sheet status.
function ComplianceOverviewTable({ data, loading, expandedIds, onToggleExpand, onOpenSupplier }) {
  if (loading) return <div className="text-muted text-center py-5">Loading…</div>;
  if (!data || data.suppliers.length === 0) {
    return <div className="text-muted text-center py-5">No suppliers yet.</div>;
  }
  const { categories, suppliers: rows } = data;
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm align-middle mb-2" style={{ minWidth: 420 + categories.length * 80 }}>
          <thead>
            <tr>
              <th style={{ minWidth: 220 }}>Manufacturer</th>
              {categories.map(c => (
                <th key={c.id} className="text-center text-muted" style={{ fontSize: 11, fontWeight: 600, minWidth: 80 }}>
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(s => {
              const expanded = expandedIds.has(s.id);
              return (
                <React.Fragment key={s.id}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => onToggleExpand(s.id)}>
                    <td>
                      <span className="text-muted me-2" style={{ display: 'inline-block', width: 10 }}>{expanded ? '▾' : '▸'}</span>
                      <span className="fw-semibold">{s.name}</span>
                      {!s.isActive && <span className="text-muted ms-1" style={{ fontSize: 11 }}>(inactive)</span>}
                      {s.ingredients.length > 0 && (
                        <span className="text-muted ms-2" style={{ fontSize: 11 }}>
                          ({s.ingredients.length} ingredient{s.ingredients.length === 1 ? '' : 's'})
                        </span>
                      )}
                    </td>
                    {categories.map(c => {
                      const isNA = FOREIGN_ONLY_KEYS.has(c.systemKey) && !s.isForeign;
                      const url = s.documentUrls?.[c.id];
                      return (
                        <td
                          key={c.id}
                          className="text-center"
                          onClick={url ? (e) => e.stopPropagation() : undefined}
                        >
                          {isNA ? (
                            <NABadge />
                          ) : url ? (
                            <a href={url} target="_blank" rel="noreferrer" title="Open document">
                              <StatusBadge status={s.statuses[c.id]} />
                            </a>
                          ) : (
                            <StatusBadge status={s.statuses[c.id]} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={categories.length + 1} className="bg-light p-0">
                        <div className="d-flex justify-content-end px-3 pt-2">
                          <button
                            className="btn btn-link btn-sm p-0"
                            style={{ fontSize: 12 }}
                            onClick={(e) => { e.stopPropagation(); onOpenSupplier(s.id); }}
                          >
                            View full profile →
                          </button>
                        </div>
                        {s.ingredients.length === 0 ? (
                          <div className="text-muted p-3 ps-4" style={{ fontSize: 12 }}>No ingredients linked.</div>
                        ) : (
                          <table className="table table-sm mb-0">
                            <tbody>
                              {s.ingredients.map(i => (
                                <tr key={i.id}>
                                  <td style={{ paddingLeft: 36, fontSize: 13 }}>{i.name}</td>
                                  <td style={{ fontSize: 12 }} className={i.hasSpecSheet ? 'text-success' : 'text-danger'}>
                                    {i.hasSpecSheet ? '✅ Spec sheet on file' : '🔴 Spec sheet missing'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <ComplianceLegend />
    </div>
  );
}

// FSVP guided checklist — see docs/SUPPLIERS_MODULE.md. Each item can be checked off, noted, and
// (when it requires one) have a document attached; the attached document is a normal Document
// (documentCategoryId = fsvp_records) so it also satisfies the top-level requirement badge.
// Who produces each checklist item — see FSVP_CHECK_DEFAULTS in backend/routes/supplierRoutes.js.
const ORIGIN_CFG = {
  importer: { label: 'You write this', cls: 'text-bg-primary' },
  supplier: { label: 'Supplier provides', cls: 'text-bg-success' },
  mixed: { label: 'Mixed', cls: 'text-bg-secondary' },
};

function OriginTag({ origin }) {
  const cfg = ORIGIN_CFG[origin];
  if (!cfg) return null;
  return <span className={`badge ${cfg.cls} ms-2`} style={{ fontSize: 10, fontWeight: 500 }}>{cfg.label}</span>;
}

function FsvpChecklistModal({ supplierId, documentCategoryId, onClose, showToast }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [uploadForItem, setUploadForItem] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadExpiryDate, setUploadExpiryDate] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);
  const fileInputRef = useRef(null);

  // silent=true skips the loading flicker on refetches after a mutation — swapping the whole
  // body out for a "Loading…" placeholder unmounts and remounts the list, which resets the
  // modal's scroll position. Only the very first load shows the spinner.
  const load = useCallback((opts = {}) => {
    if (!opts.silent) setLoading(true);
    return supplierService.getFsvpChecklist(supplierId)
      .then(setItems)
      .catch(() => showToast('Failed to load checklist.', 'danger'))
      .finally(() => { if (!opts.silent) setLoading(false); });
  }, [supplierId, showToast]);

  useEffect(() => { load(); }, [load]);

  const setItemStatus = async (item, status) => {
    setSavingId(item.templateId);
    try {
      await supplierService.saveFsvpChecklistItem(supplierId, item.templateId, { status });
      await load({ silent: true });
    } catch {
      showToast('Failed to update.', 'danger');
    } finally {
      setSavingId(null);
    }
  };

  const saveNotes = async (item, notes) => {
    try {
      await supplierService.saveFsvpChecklistItem(supplierId, item.templateId, { notes });
    } catch {
      showToast('Failed to save notes.', 'danger');
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type)) {
      showToast('Only PDF, JPEG, and PNG files are allowed.', 'danger');
      return;
    }
    setUploadFile(f);
  };

  const handleUploadSubmit = async (e, item) => {
    e.preventDefault();
    if (!uploadFile) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const { uploadUrl, publicUrl } = await documentService.getUploadUrl(uploadFile.name, uploadFile.type, uploadFile.size);
      await axios.put(uploadUrl, uploadFile, {
        headers: { 'Content-Type': uploadFile.type },
        onUploadProgress: evt => setUploadProgress(Math.round((evt.loaded / evt.total) * 100)),
      });
      // documentService.createDocument resolves { document: {...} } — see POST /api/documents.
      const { document: doc } = await documentService.createDocument({
        name: item.label,
        documentCategoryId,
        fileUrl: publicUrl,
        fileName: uploadFile.name,
        fileSize: uploadFile.size,
        mimeType: uploadFile.type,
        expiryDate: uploadExpiryDate || null,
        supplierId,
        checkResultId: item.resultId,
      });
      if (!doc?.id) throw new Error('Upload did not return a document id.');
      if (item.status !== 'completed') {
        await supplierService.saveFsvpChecklistItem(supplierId, item.templateId, { status: 'completed' });
      }
      setUploadForItem(null);
      setUploadFile(null);
      setUploadExpiryDate('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load({ silent: true });
      showToast('Document uploaded.');
    } catch {
      showToast('Upload failed.', 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    setDeletingDocId(docId);
    try {
      await documentService.deleteDocument(docId);
      await load({ silent: true });
    } catch {
      showToast('Failed to remove document.', 'danger');
    } finally {
      setDeletingDocId(null);
    }
  };

  const applicableItems = items ? items.filter(i => i.status !== 'not_applicable') : [];
  const completedCount = applicableItems.filter(i => i.status === 'completed').length;
  const naCount = items ? items.length - applicableItems.length : 0;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              FSVP Checklist{items ? (
                <span className="text-muted ms-2" style={{ fontSize: 14, fontWeight: 400 }}>
                  {completedCount}/{applicableItems.length} complete{naCount > 0 ? ` (${naCount} N/A)` : ''}
                </span>
              ) : null}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {loading || !items ? (
              <div className="text-muted text-center py-4">Loading…</div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {items.map(item => (
                  <div key={item.templateId} className={`border rounded p-3 ${item.status === 'not_applicable' ? 'bg-light' : ''}`}>
                    {item.status === 'not_applicable' ? (
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted fst-italic" style={{ fontSize: 13 }}>
                          {item.label} — Not applicable
                          {item.completedByName && <span style={{ fontSize: 11 }}> (marked by {item.completedByName})</span>}
                        </span>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0"
                          style={{ fontSize: 11 }}
                          disabled={savingId === item.templateId}
                          onClick={() => setItemStatus(item, 'pending')}
                        >
                          Undo
                        </button>
                      </div>
                    ) : (
                      <div className="d-flex align-items-start gap-2">
                        <input
                          type="checkbox"
                          className="form-check-input mt-1"
                          checked={item.status === 'completed'}
                          disabled={savingId === item.templateId}
                          onChange={() => setItemStatus(item, item.status === 'completed' ? 'pending' : 'completed')}
                        />
                        <div className="flex-grow-1">
                          <div className="fw-semibold" style={{ fontSize: 14 }}>
                            {item.label}
                            <OriginTag origin={item.origin} />
                          </div>
                          {item.description && (
                            <div className="text-muted" style={{ fontSize: 12 }}>{item.description}</div>
                          )}
                          {item.status === 'completed' && item.completedByName && (
                            <div className="text-muted" style={{ fontSize: 11 }}>Completed by {item.completedByName}</div>
                          )}
                          <textarea
                            className="form-control form-control-sm mt-2"
                            rows={1}
                            placeholder="Notes (optional)"
                            defaultValue={item.notes}
                            onBlur={e => { if (e.target.value !== item.notes) saveNotes(item, e.target.value); }}
                          />
                          {item.documents.length > 0 && (
                            <ul className="list-unstyled mt-2 mb-0 d-flex flex-column gap-1">
                              {item.documents.map(doc => (
                                <li key={doc.id} className="d-flex align-items-center gap-2">
                                  <a href={doc.fileUrl} target="_blank" rel="noreferrer"
                                    className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }}>
                                    📎 {doc.name}
                                  </a>
                                  {doc.expiryDate && <ExpiryBadge date={doc.expiryDate} />}
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm py-0 px-1"
                                    style={{ fontSize: 11, lineHeight: 1.4 }}
                                    disabled={deletingDocId === doc.id}
                                    onClick={() => handleDeleteDocument(doc.id)}
                                    title="Remove document"
                                  >
                                    &times;
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          {item.documents.length === 0 && item.requiresDocument && (
                            <div className="text-danger mt-2" style={{ fontSize: 11 }}>No document attached</div>
                          )}
                          <div className="d-flex gap-3 mt-2">
                            <button
                              className="btn btn-outline-primary btn-sm"
                              style={{ fontSize: 11 }}
                              onClick={() => { setUploadForItem(item.templateId); setUploadFile(null); setUploadExpiryDate(''); }}
                            >
                              + Attach file
                            </button>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-muted"
                              style={{ fontSize: 11 }}
                              disabled={savingId === item.templateId}
                              onClick={() => setItemStatus(item, 'not_applicable')}
                            >
                              Mark as N/A
                            </button>
                          </div>
                          {uploadForItem === item.templateId && (
                            <form onSubmit={e => handleUploadSubmit(e, item)} className="mt-2 p-2 bg-light rounded">
                              <input
                                ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                                className="form-control form-control-sm mb-2" onChange={handleFileSelect}
                              />
                              <div className="mb-2">
                                <label className="form-label form-label-sm mb-1" style={{ fontSize: 11 }}>Expiry date (optional)</label>
                                <input
                                  type="date"
                                  className="form-control form-control-sm"
                                  style={{ maxWidth: 180 }}
                                  value={uploadExpiryDate}
                                  onChange={e => setUploadExpiryDate(e.target.value)}
                                />
                              </div>
                              {uploading && (
                                <div className="progress mb-2" style={{ height: 6 }}>
                                  <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                                </div>
                              )}
                              <div className="d-flex gap-2">
                                <button type="submit" className="btn btn-primary btn-sm" disabled={uploading || !uploadFile}>
                                  {uploading ? `Uploading… ${uploadProgress}%` : 'Upload'}
                                </button>
                                <button type="button" className="btn btn-outline-secondary btn-sm"
                                onClick={() => { setUploadForItem(null); setUploadFile(null); }}>
                                Cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <a
              href={`/suppliers/fsvp-packet?id=${supplierId}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-outline-primary btn-sm me-auto"
            >
              📄 View Auditor Packet
            </a>
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SuppliersHub({ suppliers, onSuppliersChange }) {
  const [view, setView] = useState('directory'); // 'directory' | 'overview'
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [expandedOverviewIds, setExpandedOverviewIds] = useState(() => new Set());

  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSaving, setNewSaving] = useState(false);

  const [infoForm, setInfoForm] = useState(null);
  const [infoSaving, setInfoSaving] = useState(false);
  const [toggleActiveSaving, setToggleActiveSaving] = useState(false);

  const [docCategories, setDocCategories] = useState([]);
  const [uploadForRequirement, setUploadForRequirement] = useState(null); // requirement object
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadExpiryDate, setUploadExpiryDate] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [showAddRequirement, setShowAddRequirement] = useState(false);
  const [addRequirementCategoryId, setAddRequirementCategoryId] = useState('');

  const [confirmDeleteSupplier, setConfirmDeleteSupplier] = useState(false);
  const [confirmDeleteRequirement, setConfirmDeleteRequirement] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  const [fsvpChecklistFor, setFsvpChecklistFor] = useState(null); // { supplierId, documentCategoryId } | null

  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type = 'success') => setToast({ msg, type }), []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    documentCategoryService.getCategories().then(setDocCategories).catch(() => {});
  }, []);

  const loadOverview = useCallback(() => {
    setOverviewLoading(true);
    supplierService.getComplianceOverview()
      .then(setOverview)
      .catch(() => showToast('Failed to load compliance overview.', 'danger'))
      .finally(() => setOverviewLoading(false));
  }, [showToast]);

  useEffect(() => { if (view === 'overview') loadOverview(); }, [view, loadOverview]);

  const toggleOverviewExpand = useCallback((id) => {
    setExpandedOverviewIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const openSupplierFromOverview = useCallback((id) => {
    setSelectedId(id);
    setView('directory');
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    setLoadingDetail(true);
    try {
      const data = await supplierService.getSupplier(id);
      setDetail(data);
      setInfoForm({
        name: data.name || '', addressLine1: data.addressLine1 || '', addressLine2: data.addressLine2 || '',
        city: data.city || '', stateProvince: data.stateProvince || '', postalCode: data.postalCode || '',
        country: data.country || '', contactName: data.contactName || '', email: data.email || '',
        phone: data.phone || '', isForeign: data.isForeign || false, notes: data.notes || '',
        isActive: data.isActive !== false,
      });
    } catch {
      showToast('Failed to load supplier.', 'danger');
    } finally {
      setLoadingDetail(false);
    }
  }, [showToast]);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const visibleSuppliers = showInactive ? suppliers : suppliers.filter(s => s.isActive !== false);
  const filtered = search.trim()
    ? visibleSuppliers.filter(s => s.name.toLowerCase().includes(search.trim().toLowerCase()))
    : visibleSuppliers;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setNewSaving(true);
    try {
      const created = await supplierService.createSupplier({ name: newName.trim() });
      setNewName('');
      setShowNewForm(false);
      await onSuppliersChange();
      setSelectedId(created.id);
      showToast('Supplier created.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to create supplier.', 'danger');
    } finally {
      setNewSaving(false);
    }
  };

  const handleInfoSave = async () => {
    setInfoSaving(true);
    try {
      await supplierService.updateSupplier(selectedId, infoForm);
      await Promise.all([onSuppliersChange(), loadDetail(selectedId)]);
      showToast('Saved.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save.', 'danger');
    } finally {
      setInfoSaving(false);
    }
  };

  // One-click archive/reactivate — a faster path to the same isActive toggle the Info tab's
  // "Active" checkbox already saves, for when a supplier can't be hard-deleted (still linked to
  // ingredients/documents) and the user just wants it off the active list.
  const handleToggleActive = async () => {
    const nextActive = !infoForm.isActive;
    setToggleActiveSaving(true);
    try {
      await supplierService.updateSupplier(selectedId, { isActive: nextActive });
      setInfoForm(f => ({ ...f, isActive: nextActive }));
      await onSuppliersChange();
      showToast(nextActive ? 'Supplier reactivated.' : 'Supplier archived.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update supplier.', 'danger');
    } finally {
      setToggleActiveSaving(false);
    }
  };

  const handleDeleteSupplier = async () => {
    setDeleteError('');
    try {
      await supplierService.deleteSupplier(selectedId);
      setConfirmDeleteSupplier(false);
      setSelectedId(null);
      await onSuppliersChange();
      showToast('Supplier deleted.');
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Delete failed.');
    }
  };

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!ALLOWED_MIME.includes(f.type)) {
      showToast('Only PDF, JPEG, and PNG files are allowed.', 'danger');
      return;
    }
    setUploadFile(f);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadForRequirement) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const { uploadUrl, publicUrl } = await documentService.getUploadUrl(uploadFile.name, uploadFile.type, uploadFile.size);
      await axios.put(uploadUrl, uploadFile, {
        headers: { 'Content-Type': uploadFile.type },
        onUploadProgress: evt => setUploadProgress(Math.round((evt.loaded / evt.total) * 100)),
      });
      await documentService.createDocument({
        name: uploadForRequirement.category?.name || uploadFile.name,
        documentCategoryId: uploadForRequirement.category?.id,
        fileUrl: publicUrl,
        fileName: uploadFile.name,
        fileSize: uploadFile.size,
        mimeType: uploadFile.type,
        expiryDate: uploadExpiryDate || null,
        supplierId: selectedId,
      });
      showToast('Document uploaded.');
      setUploadForRequirement(null);
      setUploadFile(null);
      setUploadExpiryDate('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadDetail(selectedId);
    } catch {
      showToast('Upload failed.', 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleAddRequirement = async (e) => {
    e.preventDefault();
    if (!addRequirementCategoryId) return;
    try {
      await supplierService.addRequirement(selectedId, addRequirementCategoryId);
      setShowAddRequirement(false);
      setAddRequirementCategoryId('');
      await loadDetail(selectedId);
      showToast('Requirement added.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to add requirement.', 'danger');
    }
  };

  const handleDeleteRequirement = async () => {
    try {
      await supplierService.removeRequirement(selectedId, confirmDeleteRequirement.id);
      setConfirmDeleteRequirement(null);
      await loadDetail(selectedId);
      showToast('Requirement removed.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to remove requirement.', 'danger');
    }
  };

  const usedCategoryIds = new Set((detail?.requirements || []).map(r => r.category?.id));
  const availableCategories = docCategories.filter(c => c.isActive && !usedCategoryIds.has(c.id));

  return (
    <div className="card mb-4">
      <div className="card-header text-white d-flex align-items-center justify-content-between" style={{ backgroundColor: '#1b2638' }}>
        <strong>Suppliers</strong>
        <div className="d-flex gap-2">
          <div className="btn-group btn-group-sm" role="group">
            <button className={`btn btn-outline-light ${view === 'directory' ? 'active' : ''}`} onClick={() => setView('directory')}>
              Directory
            </button>
            <button className={`btn btn-outline-light ${view === 'overview' ? 'active' : ''}`} onClick={() => setView('overview')}>
              Compliance Overview
            </button>
          </div>
          <button className="btn btn-sm btn-outline-light" onClick={() => supplierService.exportCsv()}>
            ⬇ Export CSV
          </button>
          <button className="btn btn-sm btn-outline-light" onClick={() => { setShowNewForm(v => !v); setNewName(''); }}>
            {showNewForm ? '✕ Cancel' : '+ New Supplier'}
          </button>
        </div>
      </div>
      <div className="card-body p-0">
        {view === 'overview' && (
          <div className="p-3">
            <ComplianceOverviewTable
              data={overview}
              loading={overviewLoading}
              expandedIds={expandedOverviewIds}
              onToggleExpand={toggleOverviewExpand}
              onOpenSupplier={openSupplierFromOverview}
            />
          </div>
        )}
        {view === 'directory' && showNewForm && (
          <form onSubmit={handleCreate} className="p-3 bg-light border-bottom d-flex gap-2 align-items-center">
            <input
              className="form-control form-control-sm" style={{ maxWidth: 320 }}
              placeholder="Supplier / manufacturer name…" value={newName}
              onChange={e => setNewName(e.target.value)} autoFocus
            />
            <button className="btn btn-primary btn-sm" disabled={newSaving || !newName.trim()}>
              {newSaving ? 'Creating…' : 'Create'}
            </button>
            <span className="text-muted" style={{ fontSize: 12 }}>You can fill in address, contact, and documents next.</span>
          </form>
        )}
        {view === 'directory' && (
        <div className="row g-0" style={{ minHeight: 500 }}>
          {/* Left: list */}
          <div className="col-md-4 border-end" style={{ maxHeight: 700, overflowY: 'auto' }}>
            <div className="p-2 border-bottom">
              <input
                className="form-control form-control-sm mb-1" placeholder="Search suppliers…"
                value={search} onChange={e => setSearch(e.target.value)}
              />
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id="suppliers-show-inactive"
                  checked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)} />
                <label className="form-check-label text-muted" htmlFor="suppliers-show-inactive" style={{ fontSize: 11 }}>
                  Show inactive
                </label>
              </div>
            </div>
            {filtered.length === 0 ? (
              <div className="text-muted text-center p-3" style={{ fontSize: 13 }}>No suppliers yet.</div>
            ) : (
              <div className="list-group list-group-flush">
                {filtered.map(s => {
                  const hasIssue = s.requirementsTotal > 0 && s.requirementsSatisfied < s.requirementsTotal;
                  return (
                    <button
                      key={s.id}
                      className={`list-group-item list-group-item-action py-2 ${selectedId === s.id ? 'active' : ''}`}
                      onClick={() => setSelectedId(s.id)}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="fw-semibold" style={{ fontSize: 14 }}>
                          {hasIssue && <span className="text-danger me-1">●</span>}
                          {s.name}
                          {!s.isActive && <span className="text-muted ms-1" style={{ fontSize: 11 }}>(inactive)</span>}
                        </span>
                        <span className={`badge ${selectedId === s.id ? 'bg-light text-dark' : 'text-bg-secondary'}`} style={{ fontSize: 10 }}>
                          {s.requirementsSatisfied}/{s.requirementsTotal}
                        </span>
                      </div>
                      <div className={selectedId === s.id ? '' : 'text-muted'} style={{ fontSize: 12 }}>
                        {[s.city, s.country].filter(Boolean).join(', ') || '—'}
                        {s.ingredientCount > 0 && ` · ${s.ingredientCount} ingredient${s.ingredientCount === 1 ? '' : 's'}`}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right: detail */}
          <div className="col-md-8 p-3">
            {!selectedId ? (
              <div className="text-muted text-center py-5">Select a supplier to view details.</div>
            ) : loadingDetail || !detail ? (
              <div className="text-muted text-center py-5">Loading…</div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <h5 className="mb-0">{detail.name}</h5>
                  <div className="d-flex gap-2">
                    <button
                      className={`btn btn-sm ${infoForm.isActive ? 'btn-outline-secondary' : 'btn-outline-success'}`}
                      onClick={handleToggleActive}
                      disabled={toggleActiveSaving}
                      title={infoForm.isActive ? 'Archive — hides it from active lists without deleting it' : 'Reactivate'}
                    >
                      {toggleActiveSaving ? 'Saving…' : infoForm.isActive ? '📦 Archive' : '♻️ Reactivate'}
                    </button>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => { setDeleteError(''); setConfirmDeleteSupplier(true); }}>
                      Delete
                    </button>
                  </div>
                </div>

                {/* ── Info ── */}
                <div className="mb-4">
                  <h6 className="fw-semibold mb-2">Info</h6>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label form-label-sm">Name</label>
                      <input className="form-control form-control-sm" value={infoForm.name}
                        onChange={e => setInfoForm(f => ({ ...f, name: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label form-label-sm">Contact Person</label>
                      <input className="form-control form-control-sm" value={infoForm.contactName}
                        onChange={e => setInfoForm(f => ({ ...f, contactName: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label form-label-sm">Email</label>
                      <input type="email" className="form-control form-control-sm" value={infoForm.email}
                        onChange={e => setInfoForm(f => ({ ...f, email: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label form-label-sm">Phone</label>
                      <input className="form-control form-control-sm" value={infoForm.phone}
                        onChange={e => setInfoForm(f => ({ ...f, phone: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label form-label-sm">Address</label>
                      <input className="form-control form-control-sm" placeholder="Line 1" value={infoForm.addressLine1}
                        onChange={e => setInfoForm(f => ({ ...f, addressLine1: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label form-label-sm">City</label>
                      <input className="form-control form-control-sm" value={infoForm.city}
                        onChange={e => setInfoForm(f => ({ ...f, city: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label form-label-sm">State/Province</label>
                      <input className="form-control form-control-sm" value={infoForm.stateProvince}
                        onChange={e => setInfoForm(f => ({ ...f, stateProvince: e.target.value }))} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label form-label-sm" style={{ visibility: 'hidden' }}>Address line 2</label>
                      <input className="form-control form-control-sm" placeholder="Line 2" value={infoForm.addressLine2}
                        onChange={e => setInfoForm(f => ({ ...f, addressLine2: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label form-label-sm">Postal Code</label>
                      <input className="form-control form-control-sm" value={infoForm.postalCode}
                        onChange={e => setInfoForm(f => ({ ...f, postalCode: e.target.value }))} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label form-label-sm">Country</label>
                      <input className="form-control form-control-sm" value={infoForm.country}
                        onChange={e => setInfoForm(f => ({ ...f, country: e.target.value }))} />
                    </div>
                    <div className="col-12">
                      <label className="form-label form-label-sm">Notes</label>
                      <textarea className="form-control form-control-sm" rows={2} value={infoForm.notes}
                        onChange={e => setInfoForm(f => ({ ...f, notes: e.target.value }))} />
                    </div>
                    <div className="col-12 d-flex gap-3 align-items-center">
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="isForeign"
                          checked={infoForm.isForeign}
                          onChange={e => setInfoForm(f => ({ ...f, isForeign: e.target.checked }))} />
                        <label className="form-check-label" htmlFor="isForeign" style={{ fontSize: 13 }}>
                          Foreign / Import supplier
                        </label>
                        <div className="text-muted" style={{ fontSize: 11 }}>Adds FSVP, Prior Notice, and Foreign Supplier Verification to the compliance checklist below.</div>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="isActive"
                          checked={infoForm.isActive}
                          onChange={e => setInfoForm(f => ({ ...f, isActive: e.target.checked }))} />
                        <label className="form-check-label" htmlFor="isActive" style={{ fontSize: 13 }}>Active</label>
                      </div>
                    </div>
                  </div>
                  <button className="btn btn-primary btn-sm mt-2" onClick={handleInfoSave} disabled={infoSaving}>
                    {infoSaving ? 'Saving…' : 'Save Info'}
                  </button>
                </div>

                {/* ── Compliance Documents ── */}
                <div className="mb-4">
                  <h6 className="fw-semibold mb-2">Compliance Documents</h6>
                  <div className="d-flex flex-column gap-2">
                    {(detail.requirements || []).map(r => {
                      const isFsvp = r.category?.systemKey === 'fsvp_records';
                      return (
                        <div key={r.id} className="d-flex align-items-center justify-content-between p-2 border rounded">
                          <div>
                            <div style={{ fontSize: 13 }} className="fw-semibold">{r.category?.name || '—'}</div>
                            <StatusPill status={r.status} />
                          </div>
                          <div className="d-flex gap-1">
                            {!isFsvp && r.documents?.[0] && (
                              <a href={r.documents[0].fileUrl} target="_blank" rel="noreferrer"
                                className="btn btn-outline-secondary btn-sm" style={{ fontSize: 11 }}>View</a>
                            )}
                            {isFsvp ? (
                              <button className="btn btn-outline-primary btn-sm" style={{ fontSize: 11 }}
                                onClick={() => setFsvpChecklistFor({ supplierId: detail.id, documentCategoryId: r.category.id })}>
                                📋 Checklist
                              </button>
                            ) : (
                              <button className="btn btn-outline-primary btn-sm" style={{ fontSize: 11 }}
                                onClick={() => { setUploadForRequirement(r); setUploadFile(null); setUploadExpiryDate(''); }}>
                                Upload
                              </button>
                            )}
                            {!r.isFixed && (
                              <button className="btn btn-outline-danger btn-sm" style={{ fontSize: 11 }}
                                onClick={() => setConfirmDeleteRequirement(r)}>✕</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {uploadForRequirement && (
                    <form onSubmit={handleUploadSubmit} className="p-3 border rounded bg-light mt-2">
                      <div className="fw-semibold mb-2" style={{ fontSize: 13 }}>
                        Upload — {uploadForRequirement.category?.name}
                      </div>
                      <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png"
                        className="form-control form-control-sm mb-2" onChange={handleFileSelect} />
                      <div className="mb-2">
                        <label className="form-label form-label-sm mb-1" style={{ fontSize: 11 }}>Expiry date (optional)</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          style={{ maxWidth: 180 }}
                          value={uploadExpiryDate}
                          onChange={e => setUploadExpiryDate(e.target.value)}
                        />
                      </div>
                      {uploading && (
                        <div className="progress mb-2" style={{ height: 6 }}>
                          <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                        </div>
                      )}
                      <div className="d-flex gap-2">
                        <button type="submit" className="btn btn-primary btn-sm" disabled={uploading || !uploadFile}>
                          {uploading ? `Uploading… ${uploadProgress}%` : 'Upload'}
                        </button>
                        <button type="button" className="btn btn-outline-secondary btn-sm"
                          onClick={() => { setUploadForRequirement(null); setUploadFile(null); setUploadExpiryDate(''); }}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}

                  {showAddRequirement ? (
                    <form onSubmit={handleAddRequirement} className="d-flex gap-2 align-items-center mt-2">
                      <select className="form-select form-select-sm" style={{ maxWidth: 260 }}
                        value={addRequirementCategoryId} onChange={e => setAddRequirementCategoryId(e.target.value)}>
                        <option value="">— Select document type —</option>
                        {availableCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <button className="btn btn-primary btn-sm" disabled={!addRequirementCategoryId}>Add</button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowAddRequirement(false)}>Cancel</button>
                    </form>
                  ) : (
                    <button className="btn btn-link btn-sm p-0 mt-2" onClick={() => setShowAddRequirement(true)}>
                      + Add requirement (e.g. COI)
                    </button>
                  )}
                </div>

                {/* ── Ingredients ── */}
                <div>
                  <h6 className="fw-semibold mb-2">Ingredients</h6>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="text-muted mb-1" style={{ fontSize: 12 }}>Manufactures</div>
                      {(detail.manufacturedIngredients || []).length === 0 ? (
                        <div className="text-muted" style={{ fontSize: 12 }}>None</div>
                      ) : (
                        <ul className="list-unstyled mb-0">
                          {detail.manufacturedIngredients.map(i => (
                            <li key={i.id} className="d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
                              <span className={i.hasSpecSheet ? 'text-success' : 'text-danger'}>{i.hasSpecSheet ? '✅' : '🔴'}</span>
                              {i.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="text-muted mb-1" style={{ fontSize: 12 }}>Supplies</div>
                      {(detail.suppliedIngredients || []).length === 0 ? (
                        <div className="text-muted" style={{ fontSize: 12 }}>None</div>
                      ) : (
                        <ul className="list-unstyled mb-0">
                          {detail.suppliedIngredients.map(i => <li key={i.id} style={{ fontSize: 13 }}>{i.name}</li>)}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        )}
      </div>

      <ConfirmModal
        show={confirmDeleteSupplier}
        title="Delete Supplier"
        message={deleteError || `Delete "${detail?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDeleteSupplier}
        onCancel={() => setConfirmDeleteSupplier(false)}
      />

      <ConfirmModal
        show={!!confirmDeleteRequirement}
        title="Remove Requirement"
        message={`Remove "${confirmDeleteRequirement?.category?.name}" from this supplier's checklist?`}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleDeleteRequirement}
        onCancel={() => setConfirmDeleteRequirement(null)}
      />

      {fsvpChecklistFor && (
        <FsvpChecklistModal
          supplierId={fsvpChecklistFor.supplierId}
          documentCategoryId={fsvpChecklistFor.documentCategoryId}
          onClose={() => { setFsvpChecklistFor(null); loadDetail(selectedId); onSuppliersChange(); }}
          showToast={showToast}
        />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000, minWidth: 220, maxWidth: 340 }}>
          <div
            className="d-flex align-items-center gap-2 px-3 py-2 rounded shadow"
            style={{ background: toast.type === 'danger' ? '#dc3545' : '#198754', color: '#fff', fontSize: 13 }}
          >
            <span style={{ flex: 1 }}>{toast.msg}</span>
            <button type="button" onClick={() => setToast(null)}
              style={{ background: 'none', border: 'none', color: '#fff', opacity: 0.8, fontSize: 16, lineHeight: 1, cursor: 'pointer' }}
              aria-label="Dismiss">×</button>
          </div>
        </div>
      )}
    </div>
  );
}
